import { useEffect } from "react";
import { Box } from "@mantine/core";
import {
  DataTable, EmptyRow, Prompt, Screen, StatusDot, type Column,
  LoadError,
}  from "../components/primitives";
import { useConsoleStore } from "../stores/consoleStore";
import { useUiStore } from "../stores/uiStore";
import { useWorkspaceStore } from "../stores/workspaceStore";
import type { Operation } from "../types/domain";
import css from "./screens.module.css";

const COLUMNS: Column[] = [
  { key: "id", label: "operation_id", width: "1.4fr" },
  { key: "namespace", label: "namespace", width: "1.6fr" },
  { key: "kind", label: "kind", width: "0.8fr" },
  { key: "docs", label: "docs", width: "0.7fr" },
  { key: "attempt", label: "try", width: "0.6fr" },
  { key: "status", label: "status", width: "0.9fr" },
];

export default function Operations() {
  const openDetail = useUiStore((s) => s.openDetail);
  const workspaceId = useWorkspaceStore((s) => s.currentId);
  const operations = useConsoleStore((s) => s.operations);
  const loadError = useConsoleStore((s) => s.errors.operations);
  const summary = useConsoleStore((s) => s.operationsSummary);
  const load = useConsoleStore((s) => s.loadOperations);

  useEffect(() => {
    if (workspaceId) void load();
  }, [workspaceId, load]);

  return (
    <Screen>
      <Prompt command="vf ops watch" comment="# durable inverted ingest pipeline (M11)" />
      <LoadError message={loadError} />

      <Box className={css.tiles4} mb={16}>
        <Stat label="in flight" value={String(summary?.inFlight ?? 0)} color="var(--vf-warn)" />
        <Stat label="queued" value={String(summary?.queued ?? 0)} color="var(--vf-acc)" />
        <Stat
          label="dead-letter"
          value={String(summary?.deadLetter ?? 0)}
          color={summary?.deadLetter ? "var(--vf-bad)" : "var(--vf-ok)"}
        />
        <Box className={css.statTile}>
          <div className={css.statKey}>backend</div>
          <div style={{ color: "var(--vf-text)", fontSize: 15, paddingTop: 3 }}>
            {summary?.backend ?? "—"}
          </div>
        </Box>
      </Box>

      <DataTable<Operation>
        fontSize="12.5px"
        columns={COLUMNS}
        rows={operations}
        rowKey={(o) => o.id}
        onRowClick={(o) => openDetail("operation", o.id)}
        empty={
          <EmptyRow
            title="no operations yet"
            body="Every upsert, delete and push shows up here with its delivery attempts and commit sequence."
          />
        }
        renderCell={(o, key) => {
          switch (key) {
            case "id":
              return <span style={{ color: "var(--vf-acc)" }}>{o.id}</span>;
            case "namespace":
              return <span style={{ color: "var(--vf-text)" }}>{o.namespace}</span>;
            case "kind":
              return <span style={{ color: "var(--vf-sec)" }}>{o.kind}</span>;
            case "docs":
              return <span style={{ color: "var(--vf-sec)" }}>{o.docs}</span>;
            case "attempt":
              return <span style={{ color: "var(--vf-sec)" }}>{o.attempt}</span>;
            case "status":
              return <StatusDot status={o.status} />;
            default:
              return null;
          }
        }}
      />
    </Screen>
  );
}

function Stat({ label, value, color }: { label: string; value: string; color: string }) {
  return (
    <Box className={css.statTile}>
      <div className={css.statKey}>{label}</div>
      <div className={`${css.statValue} ${css.statValueSm}`} style={{ color }}>
        {value}
      </div>
    </Box>
  );
}
